import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Form, Row, Input, Button } from 'antd'
import { ArrowRightOutlined } from '@ant-design/icons'
import { getLoginAction } from '../../store/login/action-creators'
import { getLoadSessionAction } from '../../store/session/action-creators'
import { LOGIN_DONE } from '../../store/login/actions'
import { RESTORE_SESSION_DONE } from '../../store/session/actions'
import { IStore } from '../../store/interfaces'
import { getLocationFromRequestedQuery } from '../../../lib/utils/requested-query'
import { Logo } from '../../assets/icons/logo'
import { useAppDispatch } from '../../hooks/useAppDispatch/useAppDispatch'
import {
  LoginLayout,
  SiteLayout,
  ContentLayout,
  LoginForm,
  HeaderContainer,
  LogoContainer,
  Title,
  ErrorAlert,
  StyledFormItem,
  ButtonContainer,
  Footer,
} from './styles'

const layout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
}
const tailLayout = {
  wrapperCol: { offset: 0, span: 24 },
}

export default function LoginPage() {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { hasErrors } = useSelector((state: IStore) => state.login)
  const [loading, setLoading] = useState(false)
  const [sessionChecking, setSessionChecking] = useState(true)

  const redirectAfterLogin = useCallback(
    async (sessionFinalAction?: any) => {
      try {
        const needsPoll = Boolean(sessionFinalAction?.payload?.data?.needsPoll)
        if (needsPoll) {
          navigate('/onboarding/poll', { replace: true })
          return
        }
        const from = searchParams.get('from')
        if (from) {
          navigate(from, { replace: true })
          return
        }
        const nextLocation = getLocationFromRequestedQuery(location.search) || '/'
        navigate(nextLocation, { replace: true })
      } catch (e) {
        navigate(getLocationFromRequestedQuery(location.search) || '/', { replace: true })
      }
    },
    [location.search, navigate, searchParams],
  )

  useEffect(() => {
    let cancelled = false
    void (async () => {
      const sessionFinalAction: any = await dispatch(getLoadSessionAction())
      if (cancelled) return
      if (sessionFinalAction.type === RESTORE_SESSION_DONE) {
        await redirectAfterLogin(sessionFinalAction)
        return
      }
      setSessionChecking(false)
    })()
    return () => {
      cancelled = true
    }
  }, [dispatch, redirectAfterLogin])

  const onFinish = async values => {
    setLoading(true)
    const loginFinalAction: any = await dispatch(getLoginAction(values))
    setLoading(false)
    if (loginFinalAction.type === LOGIN_DONE) {
      const sessionFinalAction: any = await dispatch(getLoadSessionAction())
      if (sessionFinalAction.type === RESTORE_SESSION_DONE) {
        await redirectAfterLogin(sessionFinalAction)
      }
    }
  }

  const onFinishFailed = errorInfo => {
    console.error('LoginPage: form failed', errorInfo)
  }

  const currentYear = () => new Date().getFullYear()

  return (
    <LoginLayout>
      <SiteLayout className="site-layout">
        <ContentLayout>
          <Row justify="center" align="middle">
            <LoginForm
              {...layout}
              name="basic"
              layout="vertical"
              initialValues={{}}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              scrollToFirstError={true}
            >
              <HeaderContainer>
                <LogoContainer>
                  <Logo />
                </LogoContainer>
                <Title>Войти в аккаунт</Title>
                {hasErrors ? (
                  <ErrorAlert message="Ошибка авторизации" type="error" showIcon />
                ) : null}
              </HeaderContainer>
              <StyledFormItem
                label="Логин:"
                name="login"
                rules={[{ required: true, message: 'Введите e-mail пользователя' }]}
              >
                <Input />
              </StyledFormItem>
              <StyledFormItem
                label="Пароль:"
                name="password"
                rules={[{ required: true, message: 'Введите пароль' }]}
              >
                <Input.Password />
              </StyledFormItem>
              <Form.Item {...tailLayout}>
                <ButtonContainer>
                  <Button type="primary" htmlType="submit" loading={loading || sessionChecking}>
                    Войти <ArrowRightOutlined />
                  </Button>
                  <Link
                    className="login-form-forgot"
                    to="/password-recovery"
                    style={{ fontSize: '90%' }}
                  >
                    Забыли пароль?
                  </Link>
                </ButtonContainer>
              </Form.Item>
            </LoginForm>
          </Row>
        </ContentLayout>
        <Footer>&copy; 2020-{currentYear()} ООО «Аксиома». Все права защищены.</Footer>
      </SiteLayout>
    </LoginLayout>
  )
}
